/**
 * `useDemoSessionState` - GET /api/demo-sessions/current (#2824).
 *
 * What the visitor's demo knows about itself: the scenario, the change it
 * shows, whether a solve is done, and the changes the visitor can pick. The
 * "Change the scenario" button waits for `has_solved`; the guide builds one
 * tour per change. The state is read again when a solve is reported.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { subscribeDemoGuideAction } from "../components/demo/guide/events";
import { isDemoTab, readDemoSession } from "../lib/demoSession";
import { apiFetch } from "./client";
import type { components } from "./schema.gen";

export type DemoSessionStateResponse = components["schemas"]["DemoCurrentResponse"];
export type DemoSessionChange = components["schemas"]["DemoCurrentChange"];

export function useDemoSessionState() {
  const qc = useQueryClient();
  const token = readDemoSession()?.token ?? null;
  // Real tabs share localStorage with a demo tab: only a demo tab asks (#2824).
  const demoTab = isDemoTab();
  useEffect(
    () =>
      subscribeDemoGuideAction((action) => {
        if (action === "solved" || action === "change-made" || action === "member-added")
          void qc.invalidateQueries({ queryKey: ["demo-session"] });
      }),
    [qc],
  );
  return useQuery<DemoSessionStateResponse>({
    queryKey: ["demo-session", "current", token],
    queryFn: () => apiFetch<DemoSessionStateResponse>("/api/demo-sessions/current"),
    enabled: token !== null && demoTab,
    retry: false,
    staleTime: 30_000,
  });
}
